import React from "react";

import { css } from "@emotion/css";
import { CellContext, type SortingFn } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { PatientListItem } from "@/models/PatientSummaryModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Tag } from "@/uiKit/atoms/Tag.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type NoticeColumnProps = CellContext<PatientListItem, boolean>;

export const NurseNoticeColumn: React.FC<NoticeColumnProps> = ({ getValue }) => {
  const styles = useStyles(makeStyles);
  const hasNotice = getValue();

  if (!hasNotice) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Tag color="blue" icon={<Icons.forward className={styles.icon} />} className={styles.noticeTag} />
    </div>
  );
};

export const DoctorNoticeColumn: React.FC<NoticeColumnProps> = ({ getValue }) => {
  const styles = useStyles(makeStyles);
  const hasNotice = getValue();

  if (!hasNotice) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Tag color="blue" icon={<Icons.stethoscope className={styles.biggerIcon} />} className={styles.noticeTag} />
    </div>
  );
};

export const NoticeHeader: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles(makeHeaderStyles);
  return (
    <div className={styles.container}>
      <Typography>{t("pages.patients.toConsult")}</Typography>
    </div>
  );
};

export const sortByNurseNoticeForNurse: SortingFn<PatientListItem> = (rowA, rowB) => {
  const a = rowA.original.hasNurseNotice;
  const b = rowB.original.hasNurseNotice;
  return a === b ? 0 : a ? -1 : 1;
};

export const sortByNurseNoticeForDoctor: SortingFn<PatientListItem> = (rowA, rowB) => {
  const a = rowA.original.hasNurseNotice;
  const b = rowB.original.hasNurseNotice;
  return a === b ? 0 : a ? -1 : 1;
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
  `,
  noticeTag: css`
    width: ${theme.spacing(16)};
    height: ${theme.spacing(16)};
  `,
  icon: css`
    width: ${theme.typography.body.fontSize};
    height: ${theme.typography.body.fontSize};
  `,
  biggerIcon: css`
    width: ${theme.typography.subtitleLarge.fontSize};
    height: ${theme.typography.subtitleLarge.fontSize};
  `,
});

const makeHeaderStyles = () => ({
  container: css`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
  `,
});
